import { css } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import { Arrow, MenuClose } from '@fs/zion-icon'
import { i18n } from '@fs/zion-locale'
import {
  BillboardButton,
  colors,
  H2,
  IconButton,
  Image,
  InlineButton,
  LayoutBand,
  Paragraph,
  Row,
  Separator,
  useContainerWidth,
  WhiteTheme,
} from '@fs/zion-ui'
import React, { useState } from 'react'
import america250Logo from './America250Logo.svg'
import stars from './stars.svg'
import starsAndPeople from './starsAndPeople.webp'

const STORAGE_KEY = 'campaigns.america250BannerDismissed'
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000

const bannerGradientCss = css`
  background:
    var(--banner-image-url) no-repeat right,
    linear-gradient(
      90deg,
      ${colors.blue70} 62%,
      ${colors.blue50} 78%,
      ${colors.blue30} 86%,
      ${colors.blue20} 100%
    ) !important;
  background-size: contain, auto !important;
`

const getIsDismissed = () => {
  try {
    const dismissedTime = localStorage.getItem(STORAGE_KEY)
    if (!dismissedTime) return false
    const timeSinceDismissal = Date.now() - Number.parseInt(dismissedTime, 10)
    return timeSinceDismissal < ONE_MONTH_MS
  } catch {
    return false
  }
}

export default function America250Banner() {
  const { isOn, config } = useFeatureFlag('campaign_america250Banner')
  const atWidth = useContainerWidth()
  const isXl = atWidth({ default: false, xl: true })
  const [isVisible, setIsVisible] = useState(() => !getIsDismissed())

  if (!isOn || !isVisible || i18n.language !== 'en') {
    return null
  }

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString())
    setIsVisible(false)
  }

  const text = config?.text || 'Celebrate your family’s place in history during America250.'
  const url = config?.url || 'https://www.familysearch.org/en/campaign/page/america250?icid=HP-00055644'
  const buttonText = config?.buttonText || 'Learn More'
  const ctaProps = { to: url, external: true, linkName: 'America250LearnMore', target: '_blank', children: buttonText }

  return (
    <LayoutBand as="aside" marginY="none" marginX="md" paddingX="none" maxWidth="none">
      <LayoutBand
        css={bannerGradientCss}
        style={{ '--banner-image-url': `url(${atWidth({ default: '', xs: stars, xl: starsAndPeople })})` }}
        marginY="xs"
        top
        bottom
      >
        <Row alignY="middle" gutters={atWidth({ default: 'xxs', xs: 'xs', sm: 'sm', md: 'md' })} guttersY="lg">
          <Image
            src={america250Logo}
            alt="America250"
            width={atWidth({ default: 54, xs: 60, md: 72 })}
            noCrop
            noShrinkX
          />
          <WhiteTheme growX>
            {atWidth({
              default: (
                <>
                  <Paragraph as="h2" size={atWidth({ default: 'xs', md: 'sm', lg: 'md' })} balanced>
                    <strong>{text}</strong>
                  </Paragraph>
                  <Separator size="nano" />
                  <InlineButton {...ctaProps} Icon={Arrow} iconDirection="forward" iconAtEnd />
                </>
              ),
              xl: (
                <H2 size="H5" balanced>
                  {text}
                </H2>
              ),
            })}
          </WhiteTheme>
          {atWidth({
            default: null,
            xs: <div style={{ width: atWidth({ default: 20, xl: 200 }) }} noShrinkX />,
          })}
          <Row alignItemsY="middle" gutters="sm" noShrinkX>
            {isXl && <BillboardButton {...ctaProps} emphasis="lightHigh" color="gray" />}
            <IconButton
              title="Close"
              onClick={handleClose}
              Icon={MenuClose}
              emphasis="high"
              size={isXl ? 'sm' : 'xs'}
              color={isXl ? 'blue' : 'gray'}
              linkName="America250Close"
            />
          </Row>
        </Row>
      </LayoutBand>
    </LayoutBand>
  )
}
